'use client';

import styles from './ui.module.css';
import { Logo } from './brand';

export interface SiteChromeProps {
  currentPath?: string | undefined;
  bleed?: React.ReactNode;
  backdrop?: React.ReactNode;
  column?: boolean;
  surface?: boolean;
  mark?: boolean;
  children?: React.ReactNode;
}

/**
 * The chrome around a page, which is almost nothing.
 *
 * ── Navigation does not announce itself ───────────────────────────────────
 *
 * **There was a scene transition here and it is gone.** A blade of the accent
 * swept the window on every route change and `<main>` was keyed on the path, so
 * each navigation remounted the page and dealt its blocks back in on a stagger.
 * Both were written when a navigation meant a different document.
 *
 * It does not any more. `/` and `/g/<slug>` are the same room — one has a case
 * out of the pile — so the "transition" was a blue band wiping across a scene
 * that had not changed, twice per case, and the key was tearing down the
 * renderer and building it again underneath. What should look like picking
 * something up looked like the page reloading.
 *
 * So there is no wipe, no key and no arrival stagger. Moving between pages is a
 * cut, which is what this brand does everywhere else: `ident/timing.ts` argues
 * the same thing for the film's exit, and section 04 has no gradients — and a
 * dissolve is a gradient in time.
 *
 * The navigation sound went with the wipe. It was the sound of a scene
 * changing, and nothing changes scene now.
 */
export function SiteChrome({
  bleed,
  backdrop,
  column = true,
  surface = false,
  mark = true,
  children,
}: SiteChromeProps): React.ReactElement {
  return (
    <div
      className={backdrop ? `${styles.shell} ${styles.shellBackdropped}` : styles.shell}
      data-gb-space="store"
    >
      {backdrop}

      <div className={styles.shellBody}>
        <div className={surface ? styles.shellSurface : styles.shellScroll}>
          {bleed}

          <div
            className={
              surface
                ? styles.shellSurfaceMain
                : column
                  ? `${styles.page} ${styles.shellMain}`
                  : styles.shellMain
            }
          >
            {surface || !mark ? null : (
              <div className={styles.pageMark}>
                <Logo horizontal size="1.125rem" />
              </div>
            )}

            <main className={surface ? styles.pageSurface : column ? styles.pageMain : undefined}>
              {children}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
